import { Controller } from "@hotwired/stimulus"

// The Release Watch pop-up: a figure or listing loaded into its frame opens
// it; ×, Esc or a click outside closes it.
//
// Opened from a card that decides something ([data-rw-peek-source]: a merge,
// an inbox card, a "Same figure?" row), its decisions ([data-rw-peek-action]
// buttons, those showing on the card) are along the pop-up's foot, so it can
// be settled without closing it first: each closes the pop-up and presses
// the card's own button, confirm and all. One for a record
// (data-rw-peek-for="figure-12": its "Not it") shows only beside that record.
// Above them, what the card has for deciding with ([data-rw-peek-panel]),
// each field standing in for the card's own, so a change in one is made in
// the other: a <template> (a merge's Version boxes) says whose with
// data-rw-peek-mirror (a selector); a part of the card showing (an inbox
// card's merge box) is copied as it stands, field for field, and copied
// again after each change, so what the card's own controllers show or hide
// (a "New option…" box) shows here too.
export default class extends Controller {
  static targets = ["frame", "actions", "panel", "buttons"]

  connect() {
    this.remember = (event) => {
      const link = event.target.closest?.("a[data-turbo-frame='rw_peek']")
      // Every pop-up link outside it says which card it's from, or none.
      if (link && !this.element.contains(link)) this.source = link.closest("[data-rw-peek-source]")
    }
    document.addEventListener("click", this.remember, true)
  }

  disconnect() {
    document.removeEventListener("click", this.remember, true)
  }

  open() {
    if (!this.element.open) this.element.showModal()
    this.drawActions()
  }

  close() {
    this.element.close()
  }

  backdrop(event) {
    if (event.target === this.element) this.close()
  }

  // What came back isn't a pop-up (a login that ran out sends the sign-in
  // page): shown as the page it is, not left as a click that does nothing.
  missing(event) {
    event.preventDefault()
    event.detail.visit(event.detail.response)
  }

  // Emptied on close, so the next one doesn't flash the last.
  clear() {
    this.frameTarget.innerHTML = ""
    this.frameTarget.removeAttribute("src")
    this.panelTarget.replaceChildren()
    this.buttonsTarget.replaceChildren()
    this.actionsTarget.hidden = true
  }

  drawActions() {
    const source = this.source?.isConnected ? this.source : null
    const keys = this.keys()
    const originals = source ? [...source.querySelectorAll("[data-rw-peek-action]")].filter(button =>
      button.closest("[data-rw-peek-source]") === source && button.getClientRects().length > 0 &&
      (!button.dataset.rwPeekFor || keys.includes(button.dataset.rwPeekFor))) : []
    const panels = source ? [...source.querySelectorAll("[data-rw-peek-panel]")].filter(panel => panel.closest("[data-rw-peek-source]") === source &&
      (panel.tagName === "TEMPLATE" || panel.getClientRects().length > 0)) : []
    this.panelTarget.replaceChildren(...panels.map(panel => panel.tagName === "TEMPLATE" ? this.mirror(panel, source) : this.live(panel)))
    this.panelTarget.hidden = panels.length === 0
    this.buttonsTarget.replaceChildren(...originals.map(original => this.copy(original)))
    this.actionsTarget.hidden = originals.length === 0 && panels.length === 0
  }

  // A copy of part of the card, its fields passing changes back to the card's
  // (paired in order), sending nothing themselves; buttons that submit stay on
  // the card. Copied again after a change, its focus kept.
  live(original) {
    const copy = original.cloneNode(true)
    copy.querySelectorAll("[type=submit], button:not([type=button])").forEach(button => button.remove())
    const fields = (root) => [...root.querySelectorAll("input, select, textarea, button")]
    const theirs = fields(original).filter(field => !(field.type === "submit" || (field.tagName === "BUTTON" && field.type !== "button")))
    ;[copy, ...copy.querySelectorAll("*")].forEach(el => {
      el.removeAttribute("id")
      ;[...el.attributes].filter(a => a.name === "data-controller" || a.name === "data-action" || /^data-.*-target$/.test(a.name)).forEach(a => el.removeAttribute(a.name))
    })
    fields(copy).forEach((field, i) => {
      const card = theirs[i]
      if (!card) return
      field.removeAttribute("name")
      field.removeAttribute("form")
      if (field.type === "checkbox" || field.type === "radio") field.checked = card.checked
      else if (field.tagName !== "BUTTON") field.value = card.value
      const redraw = () => requestAnimationFrame(() => {
        if (!copy.isConnected) return
        const again = this.live(original)
        copy.replaceWith(again)
        const focus = fields(again)[i]
        focus?.focus()
        if (focus && typeof focus.selectionStart === "number" && typeof field.selectionStart === "number") focus.setSelectionRange(field.selectionStart, field.selectionEnd)
      })
      if (field.tagName === "BUTTON") {
        field.addEventListener("click", () => { card.click(); redraw() })
        return
      }
      const pass = (type) => {
        if (field.type === "checkbox" || field.type === "radio") card.checked = field.checked
        else card.value = field.value
        card.dispatchEvent(new Event(type, { bubbles: true }))
      }
      field.addEventListener("input", () => pass("input"))
      field.addEventListener("change", () => { pass("change"); redraw() })
    })
    copy.dataset.rwPeekCopy = ""
    return copy
  }

  // A panel's boxes, each showing its card box's value and passing changes back to it.
  mirror(template, source) {
    const panel = template.content.cloneNode(true)
    panel.querySelectorAll("[data-rw-peek-mirror]").forEach(box => {
      const original = source.querySelector(box.dataset.rwPeekMirror)
      if (!original) return (box.closest("[data-rw-peek-row]") || box).remove()
      const checkable = box.type === "checkbox" || box.type === "radio"
      // A choice the card's select gained since (a figure found by "find another figure…") comes along.
      if (box.tagName === "SELECT" && original.selectedOptions[0] && ![...box.options].some(o => o.value === original.value)) {
        box.add(new Option(original.selectedOptions[0].text, original.value))
      }
      if (checkable) box.checked = original.checked
      else box.value = original.value
      box.addEventListener(checkable ? "change" : "input", () => {
        if (checkable) original.checked = box.checked
        else original.value = box.value
        original.dispatchEvent(new Event(checkable ? "change" : "input", { bubbles: true }))
      })
    })
    return panel
  }

  copy(original) {
    const button = Object.assign(document.createElement("button"), { type: "button", className: original.className, title: original.title })
    const icon = original.querySelector("i.fas, i.fa")
    if (icon) button.append(icon.cloneNode(true), " ")
    button.append(original.dataset.rwPeekLabel || original.textContent.replace(/\s+/g, " ").trim())
    button.addEventListener("click", () => {
      this.close()
      original.click()
    })
    return button
  }

  // The records shown: keys=figure-1,listing-2 (or a and b).
  keys() {
    const src = this.frameTarget.getAttribute("src")
    if (!src) return []
    const params = new URL(src, window.location.href).searchParams
    return (params.get("keys")?.split(",") || [params.get("a"), params.get("b")]).filter(Boolean)
  }
}
